import { useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

/**
 * 案件詳細ページにおけるAI処理（インサイト・推薦文生成）の
 * 状態管理とAPI通信ロジックをカプセル化するカスタムフック。
 */
const useOpportunityAI = (opportunityId, fetchAllProposedJobs) => {
    // --- AIインサイト用State ---
    const [insightLoading, setInsightLoading] = useState(false);
    const [insightResult, setInsightResult] = useState(null);
    const [insightError, setInsightError] = useState(null);

    // --- AI履歴書レビュー用State ---
    const [resumeReviewLoading, setResumeReviewLoading] = useState(false);
    const [resumeReviewResult, setResumeReviewResult] = useState(null);
    const [resumeReviewError, setResumeReviewError] = useState(null);
    const [showResumeReviewModal, setShowResumeReviewModal] = useState(false);

    // --- AI推薦文用State ---
    const [recommendationLoading, setRecommendationLoading] = useState(false);
    const [recommendationResult, setRecommendationResult] = useState(null);
    const [recommendationError, setRecommendationError] = useState(null);
    const [showRecommendationPromptModal, setShowRecommendationPromptModal] = useState(false);

    // --- AIインサイト生成 ---
    const handleGenerateInsight = async () => {
        if (insightLoading) return;
        setInsightLoading(true);
        setInsightError(null);
        try {
            const response = await api.fetch(`/opportunities/${opportunityId}/generate_insight`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response) return;
            if (!response.ok) {
                const errorMessage = await handleApiError(response, 'インサイトの生成に失敗しました。');
                throw new Error(errorMessage);
            }
            const data = await response.json();
            setInsightResult(data.insight);
        } catch (err) {
            console.error(err);
            setInsightError(err.message);
        } finally {
            setInsightLoading(false);
        }
    };

    // --- AI履歴書レビュー生成 ---
    const handleGenerateResumeReview = async () => {
        setShowResumeReviewModal(false);
        if (resumeReviewLoading) return;
        setResumeReviewLoading(true);
        setResumeReviewError(null);
        try {
            const response = await api.fetch(`/opportunities/${opportunityId}/generate_resume_review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response) return;
            if (!response.ok) {
                const errorMessage = await handleApiError(response, '履歴書レビューの生成に失敗しました。');
                throw new Error(errorMessage);
            }
            const data = await response.json();
            setResumeReviewResult(data.review);
        } catch (err) {
            console.error(err);
            setResumeReviewError(err.message);
        } finally {
            setResumeReviewLoading(false);
        }
    };

    // --- AI推薦文生成 ---
    const handleClickGenerateRecommendation = () => {
        fetchAllProposedJobs();
        setShowRecommendationPromptModal(true);
    };

    const handleRecommendationPromptSelected = async (promptId, jobId) => {
        setShowRecommendationPromptModal(false);
        setRecommendationLoading(true);
        setRecommendationError(null);
        try {
            const params = new URLSearchParams();
            if (promptId) params.append('prompt_id', promptId);
            if (jobId) params.append('job_id', jobId);
            const queryString = params.toString();
            const url = `/opportunities/${opportunityId}/generate_recommendation${queryString ? '?' + queryString : ''}`;
            const response = await api.fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response) return;
            if (!response.ok) {
                const errorMessage = await handleApiError(response, '推薦文の生成に失敗しました。');
                throw new Error(errorMessage);
            }
            const data = await response.json();
            setRecommendationResult(data.recommendation);
        } catch (err) {
            console.error(err);
            setRecommendationError(err.message);
        } finally {
            setRecommendationLoading(false);
        }
    };

    return {
        // インサイト
        insightLoading,
        insightResult,
        setInsightResult,
        insightError,
        setInsightError,
        handleGenerateInsight,
        // 履歴書レビュー
        resumeReviewLoading,
        resumeReviewResult,
        setResumeReviewResult,
        resumeReviewError,
        setResumeReviewError,
        handleGenerateResumeReview,
        showResumeReviewModal,
        setShowResumeReviewModal,
        // 推薦文
        recommendationLoading,
        recommendationResult,
        setRecommendationResult,
        recommendationError,
        setRecommendationError,
        showRecommendationPromptModal,
        setShowRecommendationPromptModal,
        handleClickGenerateRecommendation,
        handleRecommendationPromptSelected,
    };
};

export default useOpportunityAI;
